import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, HardHat, ShieldCheck, MapPin, CalendarDays } from "lucide-react";
import { LOGO, LOGO_TINT } from "@/components/layout/Layout";
import { L } from "@/constants/labels";

// Visuel du hero : null → fond marine uni (grain léger).
// Renseigner une URL fournie par le client pour afficher une photo (droits vérifiés).
export const HERO_IMAGE = null;

const ease = [0.16, 1, 0.3, 1];
const up = (d = 0) => ({
  initial: { opacity: 0, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay: d, ease },
});

const Fact = ({ Icon, children }) => (
  <span className="inline-flex items-center gap-1.5 text-[11px] md:text-xs font-semibold text-white/85 font-body-l">
    <Icon size={13} className="text-[#E8710A]" /> {children}
  </span>
);

export const Hero = () => (
  <section
    className="relative overflow-hidden bg-[#0F2338] text-white flex flex-col max-h-[70vh] min-h-[560px] md:max-h-none md:min-h-screen grain"
    data-testid="hero"
  >
    {HERO_IMAGE ? (
      <img src={HERO_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
    ) : (
      <div className="absolute inset-0" />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-[#0F2338] via-[#0F2338]/70 to-[#0F2338]/30" />

    <div className="relative flex-1 max-w-6xl w-full mx-auto px-5 md:px-8 flex flex-col items-center text-center justify-center pt-20 pb-6 md:pt-32 md:pb-24">
      <motion.div {...up(0)} className="flex items-center justify-center gap-3">
        <img src={LOGO} style={{ ...LOGO_TINT, filter: "brightness(0) invert(1)" }} alt={L.brand.name} className="h-6 md:h-9 object-contain" />
        <span className="hidden sm:inline text-[11px] md:text-xs uppercase tracking-[0.28em] font-bold text-[#E8710A] font-mono-l" data-testid="hero-tag">
          De professionnel à professionnel
        </span>
      </motion.div>

      <motion.h1
        {...up(0.1)}
        className="font-display mt-4 md:mt-10 text-[1.75rem] leading-[1.05] sm:text-5xl lg:text-7xl font-semibold tracking-[-0.035em] max-w-4xl mx-auto"
        data-testid="hero-title"
      >
        Trouvez le bon partenaire, <span className="text-[#E8710A]">chantier par chantier.</span>
      </motion.h1>

      <motion.p {...up(0.2)} className="font-body-l mt-3 md:mt-6 text-sm sm:text-base md:text-xl text-white/75 leading-relaxed max-w-2xl mx-auto" data-testid="hero-subtitle">
        Artisans et entreprises du bâtiment se trouvent par métier, par secteur et par disponibilité.
      </motion.p>

      <motion.div {...up(0.3)} className="mt-5 md:mt-10 flex flex-col sm:flex-row justify-center gap-2.5 sm:gap-4 w-full sm:w-auto">
        <Link to="/inscription" className="btn-accent min-h-[52px] md:min-h-[60px] w-full sm:w-auto px-8 text-base group rounded-full" data-testid="hero-cta-register">
          Créer mon compte
          <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </Link>
        <Link to="/connexion" className="btn min-h-[52px] md:min-h-[60px] w-full sm:w-auto px-8 text-base rounded-full border border-white/40 text-white hover:bg-white hover:text-[#0F2338]" data-testid="hero-cta-login">
          J'ai déjà un compte
        </Link>
      </motion.div>

      {/* Promesse artisan — DANS le hero, visible sans scroll sur mobile (≤ 70vh) */}
      <motion.div {...up(0.4)} className="mt-5 md:mt-12 flex items-start gap-3 rounded-[6px] bg-[#E8710A] text-white px-4 py-3 md:px-6 md:py-4 max-w-2xl text-left" data-testid="free-strip">
        <HardHat size={20} strokeWidth={2.4} className="shrink-0 mt-0.5" />
        <div>
          <p className="font-display text-[13px] md:text-base font-semibold uppercase tracking-[0.06em] leading-snug">
            Inscription et usage gratuits. Commission de 8 % uniquement sur les chantiers réalisés via la plateforme
          </p>
          <p className="hidden sm:block mt-1 text-xs md:text-sm text-white/90 font-body-l">
            Créez votre profil, publiez vos disponibilités, candidatez aux chantiers.
          </p>
        </div>
      </motion.div>

      <motion.div {...up(0.55)} className="hidden md:flex mt-10 gap-6 justify-center">
        <Fact Icon={ShieldCheck}>Attestations à vérifier avant engagement</Fact>
        <Fact Icon={MapPin}>Recherche par secteur</Fact>
        <Fact Icon={CalendarDays}>Disponibilités en temps réel</Fact>
      </motion.div>
    </div>
  </section>
);
